import React, { useCallback, useState } from "react";
import {
  Box,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { style } from "./style";

import GBSCLogo from "@/assets/GBSCLogo.svg";

import GetImages from "@/components/GetImages";
import Button from "@/components/Button";
import MobileOTPVerify from "@/views/Onboarding/MobileOTPVerify";
import UniversalSearch from "@/views/Common/UniversalSearch";
import Confirmation from "@/components/Confirmation";
import Notification from "@/views/Common/Notification";
import ChangeLocation from "@/components/ChangeLocation";

import PatientImage from "@/assets/patient.png";

import { storage } from "@/utils/Storage";
import {
  topBarDropdownDispatchingEnums,
  languagesList,
  formatPhoneNumber,
} from "@/utils";
import { patientDropdownNavItem, patientTopNavItems } from "@/utils/common";
import useLogout from "@/utils/hooks/useLogout";
import { supportNumber } from "@/utils/Config";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

import {
  IAddPatient,
  IModalShowHide,
  IPatientExistOrNotModule,
  IProfileDropdownEnums,
} from "@/models/doctor";
import { IButtonVariant } from "@/models/button";
import { ISessionKeys, NavDropdownType } from "@/models";
import { IModalKeyEnums } from "@/models/permission";
import { IUserPermissionEnums } from "@/models/permission";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setAddPatient,
  setTopBarDropdownItems,
} from "@/store/slices/doctor/topNavigation";
import { setModuleName } from "@/store/slices/doctor/checkMobilePatientExistOrNot";
import {
  setIsShowUniversalSearch,
  setIsShowNotification,
  config,
  setIsShowChangeLocationModal,
} from "@/store/slices/config";

const PatientTopNav: React.FC = () => {
  const isLoggedIn = storage.getSessionToken(ISessionKeys.USER_ID);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    (localStorage.getItem("lang") as string) || languagesList[0].value
  );
  const [showProfile, setShowProfile] = useState(null);

  const { topBarItems } = useAppSelector((state) => state.topNavigation);
  const {
    location,
    isShowUniversalSearch,
    isShowNotification,
    isAllNotificationRead,
    inAppNotification,
    isShowChangeLocationModal,
  } = useAppSelector(config) || {};
  const { addPatient } = useAppSelector((state) => state.topNavigation);
  const { moduleName } = useAppSelector((state) => state.patientExistOrNot);

  const { logout } = useLogout();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissionRestrictionHandler = usePermissionRestriction();
  const { pathname } = useLocation();
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelected = (value: string) => {
    setSelectedLanguage(value);
    storage.setSessionToken(ISessionKeys.LANG, value);
    handleClose();
    window.location.reload();
  };

  const selectedLangLabel = languagesList
    ?.filter((data) => data.value === selectedLanguage)
    .map((lang) => lang.label);

  const handleShowHideDropDown = (type: IModalShowHide, event?: any) => {
    setShowProfile(type === IModalShowHide.HIDE ? null : event.currentTarget);
  };

  const openCloseProfileDropdown = useCallback(
    (identifier: IProfileDropdownEnums) => {
      setShowProfile(null);
      switch (identifier) {
        case IProfileDropdownEnums.MEMBER_PROFILE:
          navigate("/medical-records");
          return;
        case IProfileDropdownEnums.YOUR_ORDERS:
          navigate("/order-list");
          return;
        case IProfileDropdownEnums.CHAT:
          navigate("/chat");
          return;
        case IProfileDropdownEnums.LOGOUT:
          const isLogoutPermissionGranted = permissionRestrictionHandler(
            IModalKeyEnums.USER,
            IUserPermissionEnums.LOGOUT
          );
          if (!isLogoutPermissionGranted) return;
          dispatch(
            setTopBarDropdownItems(topBarDropdownDispatchingEnums[identifier])
          );
          return;
        default:
          dispatch(
            setTopBarDropdownItems(topBarDropdownDispatchingEnums[identifier])
          );
          return;
      }
    },
    []
  );

  return (
    <Grid item sx={style.container}>
      <Link to="/home">
        <img src={GBSCLogo} alt="logo" height="51" />
      </Link>
      <Grid item sx={style.searchInputLocationContainer}>
        <Grid
          item
          sx={style.locationNameInputContainer}
          onClick={() => dispatch(setIsShowChangeLocationModal(true))}
        >
          <GetImages name="LocationAccessSmallIcon" width="24" height="20" />
          <Typography component={"p"}>
            {location?.slice(0, 25) + (location?.length > 25 ? "..." : "")}
          </Typography>
        </Grid>
        <input
          placeholder={intl.formatMessage({
            id: "topBarSearchPlaceholder",
            defaultMessage: "Search doctors, clinics, tests here",
          })}
          onClick={() => dispatch(setIsShowUniversalSearch(true))}
        />
        <Grid item sx={style.searchIcon}>
          <GetImages name="NavSearchIcon" width="24" height="24" />
        </Grid>
      </Grid>
      <Grid item sx={style.navItemsContainer}>
        {patientTopNavItems.map((nav, index) => (
          <Grid
            item
            key={index}
            sx={{
              ...style.navItemLabel,
              ...(pathname === nav.path && style.activeNavItem),
            }}
            onClick={() => navigate(nav.path)}
          >
            <Typography component={"p"}>
              <FormattedMessage id={nav.label} defaultMessage="Nav Item" />
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid item sx={style.languageButton}>
        <Button
          variant={IButtonVariant.WHITE}
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          btnTrigger={handleClick}
        >
          <GetImages name="WorldIcon" width="20" height="20" />
          <Typography component={"p"}>
            {selectedLangLabel[0]?.slice(0, 3)}
          </Typography>
          <GetImages name="downArrowRedIcon" width="16" height="10" />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {languagesList.length > 0 &&
            languagesList.map((data) => (
              <MenuItem
                key={data.value}
                selected={data.value === selectedLanguage}
                onClick={() => handleSelected(data.value)}
              >
                {data.label}
              </MenuItem>
            ))}
        </Menu>
      </Grid>
      <Grid
        item
        sx={style.notificationContainer}
        onClick={() => dispatch(setIsShowNotification(true))}
      >
        <Grid item sx={style.notificationIconContainer}>
          <GetImages
            name={
              isAllNotificationRead || inAppNotification?.length === 0
                ? "ReadMessageBellDesktopIcon"
                : "UnReadMessageBellDesktopIcon"
            }
            width="22"
            height="27"
          />
        </Grid>
      </Grid>
      <Grid item sx={style.telephoneEmergencyContainer}>
        <Grid item sx={style.telephoneContainer}>
          <GetImages name="phone20by20red" width="21" height="21" />
          <Typography component={"p"}>
            {formatPhoneNumber(supportNumber)}
          </Typography>
        </Grid>
        <Grid item sx={style.helplineTime}>
          <Typography component={"p"}>24/7 helpline</Typography>
        </Grid>
      </Grid>
      <Grid item sx={style.loginButton}>
        {isLoggedIn ? (
          <>
            <Box sx={style.profileImage}>
              <img
                src={PatientImage}
                alt="profile"
                onClick={(event) =>
                  handleShowHideDropDown(IModalShowHide.SHOW, event)
                }
              />
            </Box>
            <Menu
              anchorEl={showProfile}
              open={!!showProfile}
              onClose={() =>
                handleShowHideDropDown(
                  IModalShowHide.HIDE,
                  NavDropdownType.PROFILE
                )
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={style.menuContainer}
            >
              {patientDropdownNavItem?.map((profile: any, index: number) => (
                <MenuItem
                  sx={style.menuItemContainer}
                  onClick={() => openCloseProfileDropdown(profile.identifier)}
                  key={index}
                >
                  <ListItemIcon>
                    <GetImages name={profile.iconName} width="24" height="20" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component={"p"} sx={style.dropdownText}>
                        <FormattedMessage
                          id={profile.text}
                          defaultMessage="Drop Down Item"
                        />
                      </Typography>
                    }
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Button
            btnTrigger={() => {
              dispatch(setModuleName(IPatientExistOrNotModule.LOGIN));
              dispatch(
                setAddPatient({
                  field: IAddPatient.MOBILE_CHECK,
                  value: true,
                })
              );
            }}
          >
            <FormattedMessage
              id="loginAndRegister"
              defaultMessage="Login / Register"
            />
          </Button>
        )}
      </Grid>
      {moduleName === IPatientExistOrNotModule.LOGIN &&
        addPatient?.mobileCheck && <MobileOTPVerify />}
      {isShowUniversalSearch && <UniversalSearch />}

      <Confirmation
        isOpen={topBarItems?.isLogoutOpen}
        closeHandler={() =>
          openCloseProfileDropdown(IProfileDropdownEnums.LOGOUT)
        }
        title="logout"
        alterMessage={"logout_alert_message"}
        actionHandler={logout}
      />
      {isShowNotification && (
        <Notification
          isOpen={isShowNotification}
          closeHandler={() => dispatch(setIsShowNotification(false))}
        />
      )}

      {isShowChangeLocationModal && (
        <ChangeLocation
          isOpen={isShowChangeLocationModal}
          onClose={() => dispatch(setIsShowChangeLocationModal(false))}
        />
      )}
    </Grid>
  );
};

export default PatientTopNav;
